import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save, Plus, Trash2, FileText } from "lucide-react";

/**
 * Admin: Footer Content
 * Manages the public site footer SEO text + brand tagline + copyright + legal text.
 * Backed by SiteSettings fields:
 *   footer_seo_title, footer_seo_paragraphs (List[str]),
 *   footer_brand_tagline, footer_copyright,
 *   footer_legal_brand, footer_legal_email, footer_legal_paragraphs (List[str])
 */
export default function ConfigFooter() {
  const { form, set, save, loading } = useSettings();

  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  const seoParagraphs = Array.isArray(form.footer_seo_paragraphs) ? form.footer_seo_paragraphs : [];
  const legalParagraphs = Array.isArray(form.footer_legal_paragraphs) ? form.footer_legal_paragraphs : [];

  const updateListItem = (key, idx, value) => {
    const list = Array.isArray(form[key]) ? [...form[key]] : [];
    list[idx] = value;
    set(key, list);
  };
  const addListItem = (key, defaultValue = "") => {
    const list = Array.isArray(form[key]) ? [...form[key]] : [];
    list.push(defaultValue);
    set(key, list);
  };
  const removeListItem = (key, idx) => {
    const list = Array.isArray(form[key]) ? [...form[key]] : [];
    list.splice(idx, 1);
    set(key, list);
  };

  return (
    <div data-testid="config-footer-page">
      <PageHeader
        title="Configuration"
        subtitle="Footer · SEO text, brand tagline, copyright, legal/license paragraphs shown at the bottom of the public site"
      />
      <ConfigTabs />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="space-y-5 max-w-4xl"
        data-testid="config-footer-form"
      >
        {/* SEO Title */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-emerald-600" />
            <h3 className="text-sm font-semibold text-gray-900">SEO Heading</h3>
          </div>
          <input
            value={form.footer_seo_title || ""}
            onChange={(e) => set("footer_seo_title", e.target.value)}
            className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
            placeholder="Maxlive88 Bangladesh — Leading Online Gaming and Betting Platform in Bangladesh"
            data-testid="cfg-footer_seo_title"
          />
        </div>

        {/* SEO Paragraphs */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">SEO Paragraphs</h3>
            <button
              type="button"
              onClick={() => addListItem("footer_seo_paragraphs", "")}
              className="h-8 px-3 inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold hover:bg-emerald-100"
              data-testid="cfg-add-seo-paragraph"
            >
              <Plus size={13} /> Add paragraph
            </button>
          </div>
          <div className="space-y-2.5">
            {seoParagraphs.length === 0 && (
              <p className="text-xs text-gray-400 italic">No paragraphs yet. Click "Add paragraph" to start.</p>
            )}
            {seoParagraphs.map((p, idx) => (
              <div key={idx} className="flex gap-2">
                <textarea
                  value={p}
                  onChange={(e) => updateListItem("footer_seo_paragraphs", idx, e.target.value)}
                  rows={3}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-sm text-sm resize-y"
                  placeholder={`Paragraph ${idx + 1}`}
                  data-testid={`cfg-seo-paragraph-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeListItem("footer_seo_paragraphs", idx)}
                  className="h-9 w-9 flex items-center justify-center bg-red-50 text-red-700 border border-red-200 rounded-sm hover:bg-red-100"
                  aria-label="remove"
                  data-testid={`cfg-remove-seo-paragraph-${idx}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Tagline + Copyright */}
        <div className="bg-white border border-gray-200 rounded-sm p-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Brand Tagline</label>
            <input
              value={form.footer_brand_tagline || ""}
              onChange={(e) => set("footer_brand_tagline", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              placeholder="Play Smart, Win Big"
              data-testid="cfg-footer_brand_tagline"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Copyright Line</label>
            <input
              value={form.footer_copyright || ""}
              onChange={(e) => set("footer_copyright", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              placeholder="© 2026 Maxlive88 Copyrights. All Rights Reserved"
              data-testid="cfg-footer_copyright"
            />
          </div>
        </div>

        {/* Legal Brand + Email */}
        <div className="bg-white border border-gray-200 rounded-sm p-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Legal Brand Name (used in legal paragraphs)</label>
            <input
              value={form.footer_legal_brand || ""}
              onChange={(e) => set("footer_legal_brand", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
              placeholder="Maxlive88.com"
              data-testid="cfg-footer_legal_brand"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              In legal paragraphs, write <code className="bg-gray-100 px-1 rounded">{"{brand}"}</code> wherever this name should appear.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Legal Contact Email</label>
            <input
              value={form.footer_legal_email || ""}
              onChange={(e) => set("footer_legal_email", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
              placeholder="legal@maxlive88.com"
              data-testid="cfg-footer_legal_email"
            />
          </div>
        </div>

        {/* Legal Paragraphs */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Legal / License Paragraphs</h3>
            <button
              type="button"
              onClick={() => addListItem("footer_legal_paragraphs", "")}
              className="h-8 px-3 inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold hover:bg-emerald-100"
              data-testid="cfg-add-legal-paragraph"
            >
              <Plus size={13} /> Add paragraph
            </button>
          </div>
          <div className="space-y-2.5">
            {legalParagraphs.length === 0 && (
              <p className="text-xs text-gray-400 italic">No legal paragraphs yet.</p>
            )}
            {legalParagraphs.map((p, idx) => (
              <div key={idx} className="flex gap-2">
                <textarea
                  value={p}
                  onChange={(e) => updateListItem("footer_legal_paragraphs", idx, e.target.value)}
                  rows={4}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-sm text-sm resize-y"
                  placeholder={`Use {brand} where the legal brand name should appear.`}
                  data-testid={`cfg-legal-paragraph-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeListItem("footer_legal_paragraphs", idx)}
                  className="h-9 w-9 flex items-center justify-center bg-red-50 text-red-700 border border-red-200 rounded-sm hover:bg-red-100"
                  aria-label="remove"
                  data-testid={`cfg-remove-legal-paragraph-${idx}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={loading}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-footer-save"
          >
            <Save size={14} /> {loading ? "Saving..." : "Save Footer Content"}
          </button>
        </div>
      </form>
    </div>
  );
}
